import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { PtsPaymentTerminalService } from 'src/app/shared/services/payment-terminal.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { PaymentTerminalConnectorRequest, PtsPaymentTerminal } from '../../pts-payment-terminal-model';
import { Organization, OrganizationSearchEvent } from 'src/app/features/organization/organization-models';
import { ConnectorSearchEvent } from 'src/app/features/charging-station-location/charging-station-location-model';
import { startWith, debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { ConnectorSelectorComponent } from 'src/app/shared/components/connector-selector/connector-selector.component';
import { PageEvent } from '@angular/material/paginator';
import OrganizationService from 'src/app/shared/services/organization.service';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'adm-pts-terminal-connector-relation',
  templateUrl: './terminal-connector-relation-dialog.component.html',
  styleUrl: './terminal-connector-relation-dialog.component.scss',
  imports: [
    CustomDropDownArrowDirective,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatBadgeModule,
    MatButtonModule,
  ],
})
export class PtsTerminalConnectorRelationDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<PtsTerminalConnectorRelationDialogComponent>>(MatDialogRef);
  data = inject<{
    paymentTerminal: PtsPaymentTerminal;
    organizationId: number;
    chargingStationIdentifier: string;
    fromTerminalConnectorRelationModal: boolean;
  }>(MAT_DIALOG_DATA);
  private ptsPaymentTerminalService = inject(PtsPaymentTerminalService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);
  private organizationService = inject(OrganizationService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  paymentTerminal: PtsPaymentTerminal;
  chargingStationIdentifier: string;
  fromTerminalConnectorRelationModal: boolean;
  wasSaved = false;
  saving = false;
  paymentTerminalConnectorRelationForm: FormGroup<PaymentTerminalConnectorRelationForm> =
    new FormGroup<PaymentTerminalConnectorRelationForm>({
      connectorIdentifiers: new FormControl<string[]>([], { nonNullable: true }),
      serialNumber: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
    });
  typeAheadModel = new FormGroup({
    typeAheadQuery: new FormControl<string | Organization | null>(''),
  });
  selectedOrganization: Organization;
  organizations: Organization[];
  filteredOrganizations: Organization[];
  selectedConnectorIdentifiers: string[];
  connectorBadgeCount = 0;
  organizationId: number | null = null;
  connectorSearchEvent: ConnectorSearchEvent = {} as ConnectorSearchEvent;
  private organizationSelectedByUser = false;

  get connectorIdentifiers() {
    return this.paymentTerminalConnectorRelationForm.controls['connectorIdentifiers'];
  }

  get serialNumber() {
    return this.paymentTerminalConnectorRelationForm.controls['serialNumber'];
  }
  get typeAheadQuery() {
    return this.typeAheadModel.controls['typeAheadQuery'];
  }

  async ngOnInit(): Promise<void> {
    this.paymentTerminal = this.data.paymentTerminal;
    this.organizationId = this.data.organizationId;
    this.chargingStationIdentifier = this.data.chargingStationIdentifier;
    this.fromTerminalConnectorRelationModal = this.data.fromTerminalConnectorRelationModal;

    this.initTypeAhead();
    await this.getOrganizations();
    await this.getPayterTerminalConnectors();
    this.syncTypeAheadQuery();
    this.updateForm(this.paymentTerminal);

    if (this.organizationId && this.chargingStationIdentifier) {
      this.openConnectorSelector();
    }
  }
  updateForm(paymentTerminal = {} as PtsPaymentTerminal) {
    this.paymentTerminalConnectorRelationForm.controls.serialNumber.setValue(paymentTerminal?.serialNumber ?? '');
  }
  savePaymentTerminalConnectorRelation() {
    const formValue = this.paymentTerminalConnectorRelationForm.getRawValue();
    const paymentTerminalConnectorRequest = {
      connectorIdentifiers: this.connectorIdentifiers.value,
    } as PaymentTerminalConnectorRequest;

    this.ptsPaymentTerminalService.savePaymentTerminalConnectorRelation(formValue.serialNumber, paymentTerminalConnectorRequest).subscribe({
      next: async () => {
        this.ptsPaymentTerminalService.getPaymentTerminal(this.paymentTerminal.identifier).subscribe(paymentTerminal => {
          this.dialogRef.close(paymentTerminal);
        });
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.error?.code === 170019) {
          this.snackbarService.error('Ikke alle ladepunkter blev gemt.');
        } else if (httpError.error?.code === 170021) {
          this.snackbarService.error('De valgte ladepunkter må kun være tilknyttet én lokation.');
        } else if (httpError.error?.code === 170022) {
          this.snackbarService.error(
            'Det var ikke muligt at tilknytte en terminal til ladepunktet, da ladepunktet ikke findes i databasen.'
          );
        } else {
          this.snackbarService.error(httpError.error?.message);
        }
      },
    });
  }

  public close() {
    this.dialogRef.close();
  }

  public openConnectorSelector() {
    if (!this.organizationId) {
      return;
    }
    const dialogRef = this.dialog.open(ConnectorSelectorComponent, {
      data: {
        organizationId: this.organizationId,
        selectedConnectorIdentifiers: this.connectorIdentifiers.value,
        chargingStationIdentifier: this.chargingStationIdentifier,
        fromTerminalConnectorRelationModal: this.fromTerminalConnectorRelationModal,
        terminalSerialNumber: this.paymentTerminal.serialNumber,
      },
      width: '850px',
    });

    dialogRef.afterClosed().subscribe((selectedConnectorIdentifiers: string[]) => {
      if (selectedConnectorIdentifiers) {
        this.selectedConnectorIdentifiers = selectedConnectorIdentifiers;
        this.connectorBadgeCount = selectedConnectorIdentifiers.length;
        this.connectorIdentifiers.setValue(selectedConnectorIdentifiers);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  public resetOrganizationTypeAhead() {
    this.filteredOrganizations = this.organizations;
    this.typeAheadQuery.setValue('');
    this.organizationId = null;
  }

  public selectOrganization(organization: Organization) {
    if (this.organizationId !== organization.id) {
      this.resetConnectors();
    }
    this.selectedOrganization = organization;
    this.organizationId = organization.id;
    this.organizationSelectedByUser = true;
  }

  public initTypeAhead() {
    let typeAheadQuery: string | Organization = '';
    if (this.organizationId) {
      // organizations may not be loaded yet when this runs; syncTypeAheadQuery() sets the real value later
      typeAheadQuery = this.organizations?.find(o => o.id === this.organizationId)?.name ?? '';
    }
    const typeAheadFormGroup = new FormGroup({
      typeAheadQuery: new FormControl(typeAheadQuery),
    });

    this.typeAheadModel.valueChanges
      .pipe(startWith(this.typeAheadModel.getRawValue()), debounceTime(300), distinctUntilChanged())
      .subscribe(val => {
        const typeAheadValue = val.typeAheadQuery;
        if (typeof typeAheadValue === 'string') {
          this.filteredOrganizations = typeAheadValue
            ? this.organizations.filter(o => o.name.toLowerCase().includes(typeAheadValue.toLowerCase()))
            : this.organizations;
        }
      });
  }

  private syncTypeAheadQuery() {
    const organization = this.organizations.find(o => o.id === this.organizationId) ?? null;
    this.selectedOrganization = organization ?? this.selectedOrganization;
    this.typeAheadQuery.setValue(organization);
  }

  public resetConnectors() {
    this.selectedConnectorIdentifiers = [];
    this.connectorIdentifiers.setValue([]);
    this.connectorBadgeCount = 0;
  }

  public displayFn(organization: Organization | string | null): string {
    if (!organization) return '';
    return typeof organization === 'string' ? organization : organization.name;
  }

  private async getOrganizations() {
    const searchEvent: OrganizationSearchEvent = {
      paging: new PageEvent(),
      sorting: null,
    };
    searchEvent.paging.pageIndex = 0;
    searchEvent.paging.pageSize = 10000;
    const organizations = (await firstValueFrom(this.organizationService.getOrganizations(searchEvent))).items;

    this.organizations = organizations;
    this.filteredOrganizations = this.organizations;
  }

  private async getPayterTerminalConnectors() {
    try {
      const connectors = await firstValueFrom(
        this.ptsPaymentTerminalService.getPayterTerminalConnectors(this.paymentTerminal.serialNumber)
      );
      if (connectors.length > 0 && !this.organizationSelectedByUser) {
        this.organizationId = connectors[0]?.organizationId;
        this.connectorIdentifiers.setValue(connectors.map(c => c.connectorIdentifier.toString()));
        this.connectorBadgeCount = this.connectorIdentifiers.value.length;
      }
    } catch (err) {
      const backendMessage =
        typeof err === 'object' &&
        err !== null &&
        'error' in err &&
        typeof err.error === 'object' &&
        err.error !== null &&
        'message' in err.error &&
        typeof err.error.message === 'string'
          ? err.error.message
          : null;

      this.snackbarService.error(backendMessage ?? 'Kunne ikke hente relationer for betalingsterminalen.');
    }
  }
}

export interface PaymentTerminalConnectorRelationForm {
  connectorIdentifiers: FormControl<string[]>;
  serialNumber: FormControl<string>;
}
